/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {areEqual} from 'react-window';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import {makeStyles} from '@material-ui/core/styles';
import dynamic from 'next/dynamic';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalSearchResult} from '@ion/core/src/services/GlobalSearch';
import {Account, GeoFenceStatus, User} from '@ion/core/src/types/entities';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {MapLayerIcon} from '@ion/dls/src/icons/MapLayerIcon';
import {LabelContentClient} from '@ion/dls/src/maps/markers/AssetMarkers';
import IonPortal from '@ion/dls/src/render/IonPortal';
import {
  HistoryInputEvents,
  HistoryReportTypeEnum,
  IconSize,
  MapLayerSettings,
  ToolbarEnum
} from '@ion/dls/src/types/types';
import {addComponent, removeComponent} from '../helpers/simpleMapUtils';
import {useShowTool} from '../hooks/useShowTool';
import {MapToolPositions, MapTools} from '../types/types';

// Since we're using Next.js, we wrap the Layer chooser import dynamically and set
// ssr to false, as this component contains references to the window global
const LayerChooser = dynamic(() => import('@ion/dls/src/maps/layers/LayerChooser'),
  {ssr: false}
);

// defines the LayerChooserTool prop definitions...
export interface LayerChooserToolProps{
  isSelected?: boolean;
  onClick: MouseEventHandler;
  size: IconSize;
};

// defines the empty object...
type EmptyObject = {};

// defines the prop definition for LayerChooserToolFactory component...
export interface LayerChooserToolFactoryProps {
  map: __esri.Map;
  view: __esri.MapView;
  position?: MapToolPositions;
  open?: boolean;
  onClose?: Function;
  onLayersChange: Function;
  selectedLayers: string[];
  user: User;
  accounts: Account[];
  mapLayerSettings: MapLayerSettings[];
  activeTool: MapTools;
  setActiveTool: Function;
  trafficLayerSettingProps?: {
    trafficLayerURL?: string;
    onTrafficOpacityChange: Function;
    trafficOpacity: number;
  } | EmptyObject;
  weatherLayerSettingProps?: {
    weatherLayerURL?: string;
    onWeatherOpacityChange: Function;
    weatherOpacity: number;
  } | EmptyObject;
  geofenceLayerSettingProps?: {
    onGeofenceOpacityChange: Function;
    onGeofenceBorderOnlyChange: Function;
    geofenceBorderOnly: boolean;
    geofenceOpacity: number;
    editGeofenceGraphic: any;
    setEditGeofenceGraphic: Function;
    savedGeofence: Function;
    geofenceStatus: GeoFenceStatus;
    searchResult?: GlobalSearchResult | null;
    canEditGeofence: boolean;
    selectedGeoFences: string[];
  } | EmptyObject;
  currentLayerSettingProps?: ({
    onToggleClustering: Function;
    onToggleLabelling: Function;
    isClusteringActive: boolean;
  } & LabelContentClient) | EmptyObject;
  operationLayerSettingProps?: {
    operationalLayers: string[];
    onLayersOpacityChange: Function;
    onLayerVisibilityChange: Function;
    onSublayerVisibilityChange: Function;
    islayerVisibilityUpdate: boolean;
  } | EmptyObject;
  tripLayerSettingProps?: {
    assetsHistoryLoading: boolean;
    loadAssetHistory: Function;
    setAssetsHistoryLive: Function;
    setHistoryInterval: Function;
    setHistoryInputEvents: Function;
    historyIntervalInHours: number;
    historyReportType: HistoryReportTypeEnum;
    historyInputEvents: HistoryInputEvents[];
    setHistoryReportType: Function;
    setFindNearbyPopupForGeofence: Function;
  } | EmptyObject;
};

// defining style for CreateGeofenceTool components...
const useStyles = makeStyles(theme => ({
  root : {
    position : 'absolute',
    top      : 100,
    right    : '55px'
  }
}));

/**
   * The LayerChooserTool provides the UI for layer chooser option pop-up on map and
   * that can be placed on the map layers.
   *
   * ```typescript
   *
   *   <LayersTool size={} onClick={function() {}} />
   *
   * ```
   */
export const LayersTool = ({size, onClick, isSelected = false}: LayerChooserToolProps) => {
  return (
    <FloatingIconButtonGroup>
      <FloatingIconButtonGroup.FloatingIconButton onClick={onClick}
        isSelected={isSelected}>
        <MapLayerIcon size={size} />
      </FloatingIconButtonGroup.FloatingIconButton>
    </FloatingIconButtonGroup>
  );
};

/**
   * The LayerChooserToolFactory component is responsible for creating a Layer chooser Tool/widget
   * component that provide an option to select the layer that user want. This is a stand-alone
   * tool, can be usable in any of position on the map.
   *
   * ```typescript
   *
   *  import LayerChooserToolFactory from '../tools/LayerChooserTool';
   *
   *  <LayerChooserToolFactory view={view} {..props} />
   *
   * ```
   */
export const LayerChooserToolFactory = ({
  map,
  view,
  position,
  open,
  onLayersChange,
  selectedLayers,
  user,
  accounts,
  mapLayerSettings,
  activeTool,
  setActiveTool,
  trafficLayerSettingProps = {},
  weatherLayerSettingProps = {},
  geofenceLayerSettingProps = {},
  currentLayerSettingProps = {},
  operationLayerSettingProps = {},
  tripLayerSettingProps = {}
}: LayerChooserToolFactoryProps) => {
  const classes                                = useStyles();
  const [selectedTool, setSelectedTool]        = useState<ToolbarEnum | null>(null);
  const childRef                               = useRef(null);
  const isPhone                                = useIsPhone();
  const [show, toggleShow, handleClickOutside] = useShowTool({
    ref          : childRef,
    initialState : open,
    toolId       : MapTools.layerChooser,
    activeTool,
    setActiveTool
  });

  const onClose = useCallback(() => {
    if (selectedTool !== ToolbarEnum.Layers) {
      setSelectedTool(ToolbarEnum.Layers);
    } else {
      setSelectedTool(null);
    }
  }, [selectedTool]);

  useEffect(() => {
    if (show || open) {
      addComponent({
        id        : MapTools.mapLayerChooser,
        component : (
          <ClickAwayListener onClickAway={handleClickOutside}>
            <div ref={childRef}>
              <IonPortal id='map-layer-chooser' show={show}>
                <LayerChooser map={map}
                  mapLayerSettings={mapLayerSettings}
                  mapView={view}
                  onClose={onClose}
                  onLayersChange={onLayersChange}
                  selectedLayers={selectedLayers}
                  user={user}
                  accounts={accounts}
                  externalClasses={classes}
                  show
                  {...trafficLayerSettingProps}
                  {...weatherLayerSettingProps}
                  {...geofenceLayerSettingProps}
                  {...currentLayerSettingProps}
                  {...operationLayerSettingProps}
                  {...tripLayerSettingProps} />
              </IonPortal>
            </div>
          </ClickAwayListener>
        ),
        position,
        view,
        index : 2
      });
    } else {
      removeComponent(MapTools.mapLayerChooser, view);
    }
  }, [show, open, operationLayerSettingProps]);

  return (
    <Tooltip title={translate('maps:controls.layers')} renderOnlyChildren={isPhone}>
      <div>
        <LayersTool size={IconSize.medium}
          onClick={toggleShow}
          isSelected={show} />
      </div>
    </Tooltip>
  );
};

export default memo(LayerChooserToolFactory, areEqual);
